import React, { useId } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTheme } from '@mui/material/styles';
import { VarName, varNameDetails } from '../../../utils/varNames';
import useStyles from '../../../styles/alertForm';
import ThresholdFormSection from './ThresholdFormSection';

import {
  GatewaySensorType,
  Analysisoptions as LocationAnalysisOptions,
  Analysisoptions1 as SensorAnalysisOptions,
  SensorWiFiConfOptionsAnalysisTypeEnum,
} from '../../../services/api';
import AnalysisPeriod from './AnalysisPeriodFormSection';
import SensorsFilterSection from './SensorsFilterFormSection';
import { useAppSelector } from '../../../state/store';
import { getSensorsById } from '../../../state/selectors';

export type AnalysisOptions = SensorAnalysisOptions | LocationAnalysisOptions;
export type HistoryAnalysisOptions = Extract<
  AnalysisOptions,
  { analysisType: 'locationHistory' | 'sensorHistory' }
>;

const LOCATION_ANALYSIS_TYPES = [
  { value: 'locationRecent' as const, label: 'Live Data' },
  { value: 'locationHistory' as const, label: 'History Data' },
  { value: 'sensorWiFiConf' as const, label: 'Wifi Configuration' },
];

const SENSOR_ANALYSIS_TYPES = [
  { value: 'sensorRecent' as const, label: 'Live Data' },
  { value: 'sensorHistory' as const, label: 'History Data' },
  { value: 'sensorWiFiConf' as const, label: 'Wifi Configuration' },
];

interface AnalysisOptionsContainerProps {
  checkListId: string;
  isLocation: boolean;
  value: AnalysisOptions;
  onChange: (value: Partial<AnalysisOptions>) => void;
  allowEditing: boolean;
}

function AnalysisOptionsContainer({
  checkListId,
  isLocation,
  value,
  onChange,
  allowEditing,
}: AnalysisOptionsContainerProps) {
  const uid = useId();
  const { classes } = useStyles();
  const theme = useTheme();
  const locValue = isLocation ? (value as LocationAnalysisOptions) : null;
  const sensorsById = useAppSelector(getSensorsById);

  const handleChange = (key: string, val: unknown) => {
    onChange({ [key]: val } as Partial<AnalysisOptions>);
  };

  const getSensorAnalysisTypes = () => {
    const sensorDetails = sensorsById.get(checkListId);
    const isGateway = Object.values(GatewaySensorType).includes(
      sensorDetails?.type as GatewaySensorType
    );

    return SENSOR_ANALYSIS_TYPES.filter(
      (type) => type.value !== SensorWiFiConfOptionsAnalysisTypeEnum.SensorWiFiConf || isGateway
    );
  };

  const analysisTypes = isLocation ? LOCATION_ANALYSIS_TYPES : getSensorAnalysisTypes();
  const isHistoryType =
    value.analysisType === 'locationHistory' || value.analysisType === 'sensorHistory';

  const isWifiConf = value.analysisType === SensorWiFiConfOptionsAnalysisTypeEnum.SensorWiFiConf;

  return (
    <Box>
      <Box className={classes.formContainer}>
        <InputLabel sx={{ mb: 2 }}>Select Analysis Options for </InputLabel>
        <Grid container spacing={2} sx={{ mt: 4 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel id={`${uid}-varName`} sx={{ display: 'flex', alignItems: 'center' }}>
                Variable
              </InputLabel>
              <Select
                labelId={`${uid}-varName`}
                label="Variable Name"
                value={!isWifiConf ? value.varName : VarName.ClientsWiFi}
                onChange={(e) => handleChange('varName', e.target.value as VarName)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.contrastText,
                  },
                }}
                disabled={!allowEditing}
              >
                {Object.values(VarName).map((v) => (
                  <MenuItem key={v} value={v}>
                    {varNameDetails[v]?.label ?? v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <Tooltip title="Set alert for live data or history data" placement="top" arrow>
                <InputLabel
                  id={`${uid}-analysisType`}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  Analysis Type <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} />
                </InputLabel>
              </Tooltip>
              <Select
                labelId={`${uid}-analysisType`}
                label={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Analysis Type
                    <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                  </Box>
                }
                value={value.analysisType}
                onChange={(e) => handleChange('analysisType', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.contrastText,
                  },
                }}
                disabled={!allowEditing}
              >
                {analysisTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {!isWifiConf && (
        <ThresholdFormSection
          isHistoryType={isHistoryType}
          value={value}
          handleChange={handleChange}
          allowEditing={allowEditing}
          varName={value.varName}
        />
      )}

      {isHistoryType && (
        <AnalysisPeriod value={value} handleChange={handleChange} allowEditing={allowEditing} />
      )}

      {isLocation && (
        <SensorsFilterSection locValue={locValue} onChange={onChange} allowEditing={allowEditing} />
      )}
    </Box>
  );
}

export default AnalysisOptionsContainer;
